import Day from '@awesomecomponents/day-js';
import { ActionButton, Button, Card, H1, colors } from '@manulife/mux';
import { Edit as EditIcon } from '@manulife/mux-cds-icons';
import { Route, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useAdobeAnalyticsPage from '../../CustomHooks/useAdobeAnalyticsPage';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import Layout from '../Layout/Layout';
import CardGridLoadingIndicator from '../LoadingIndicator/CardGridLoadingIndicator';
import ListLoadingIndicator from '../LoadingIndicator/ListLoadingIndicator';
import { CardGrid, CardHeaderFlex, PaddedCard, Tag } from '../Shared/SharedStyles';
import { ADOBE, APP_ROUTES } from './../../utils/AppConstants';
import BankingModal from './BankingModal';
import ContactInfoModal from './ContactInfoModal';
import EmailAddressModal from './EmailAddressModal';

const BankLabelWrapper = styled.div`
    display: grid;
    grid-template-columns: max-content max-content max-content;
    column-gap: 12px;
`;

const BoldLabel = styled.strong`
    display: block;
    margin-top: ${({ noMarginTop }) => (noMarginTop ? '0px' : '16px')};
`;

const CtaMsg = styled.div`
    color: #ff0000;
    font-weight: 800;
`;

const AddressLine2Text = styled.p`
    margin: 0;
`;
const AddressLine3Text = styled.p`
    margin-top: 0;
`;

const Personal = () => {
    const { language, isLoadingFeatures, isLoadingMemberData, features, member, isCiamSession, isSteppedUp } =
        useShallowEqualSelector((state) => ({
            language: state.Authentication.language,
            isLoadingFeatures: state.Features.isLoading,
            isLoadingMemberData: state.MemberData.isLoadingMemberData,
            features: state.Features.data,
            member: state.MemberData.member,
            isCiamSession: state.Authentication.isCiamSession,
            isSteppedUp: state.Authentication.isSteppedUp,
        }));
    const { sendAdobeAnalyticsTransaction } = useAdobeAnalyticsPage(
        'member-profile:personal',
        'Member Profile - My personal information'
    );
    const history = useHistory();

    const myPersonalTitle = useAEMContent(FRAGMENT_MP.landing, 'myPersonalInfoTitle');
    const myPersonalHOne = useAEMContent(FRAGMENT_MP.landing, 'myPersonalInfoHOne');
    const contactPAMessage = useAEMContent(FRAGMENT_MP.landing, 'contactPAMessagePersonal', { parseHTML: true });
    const updateYourPostalAddress = useAEMContent(FRAGMENT_MP.landing, 'updateYourPostalAddress');
    const updateYourBankAccount = useAEMContent(FRAGMENT_MP.landing, 'updateYourBankAccount');
    const updateYourEmailAddress = useAEMContent(FRAGMENT_MP.landing, 'updateYourEmailAddress');
    const updateYourPhoneNumber = useAEMContent(FRAGMENT_MP.landing, 'updateYourPhoneNumber');

    const personalTag = useAEMContent(FRAGMENT_MP.common, 'you');
    const dateOfBirthLabel = useAEMContent(FRAGMENT_MP.common, 'dateOfBirthLabel');
    const editButton = useAEMContent(FRAGMENT_MP.common, 'editButton');

    const myContactInfo = useAEMContent(FRAGMENT_MP.contactInfo, 'header');
    const addressLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'addressLabel');
    const phoneNumberLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'phoneNumberLabel');
    const addAddressButton = useAEMContent(FRAGMENT_MP.contactInfo, 'addAddressButton');
    const addPhoneNumberButton = useAEMContent(FRAGMENT_MP.contactInfo, 'addPhoneNumberButton');
    const contactPaMessage = useAEMContent(FRAGMENT_MP.contactInfo, 'contactPaMessage');

    const myBankingInfo = useAEMContent(FRAGMENT_MP.bankInfo, 'header');
    const bankNameLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'bankNameLabel');
    const branchLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'branchLabel');
    const institutionLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'institutionLabel');
    const accountLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'accountLabel');
    const addBankButton = useAEMContent(FRAGMENT_MP.bankInfo, 'addBankButton');
    const bankPaMessage = useAEMContent(FRAGMENT_MP.bankInfo, 'contactPaMessage');

    const myEmailAddressInfo = useAEMContent(FRAGMENT_MP.email, 'header');
    const emailAddressLabel = useAEMContent(FRAGMENT_MP.email, 'emailAddressLabel');
    const addEmailButton = useAEMContent(FRAGMENT_MP.email, 'addEmailButton');
    const emailPaMessage = useAEMContent(FRAGMENT_MP.email, 'contactPaMessage');

    const openModal = (modalName) => {
        // If member has not stepped up, trigger step up authentication
        if (isCiamSession && !isSteppedUp) {
            return history.push(modalName.stepUpUrl);
        }

        return history.push(modalName.url);
    };

    return (
        <Layout pageTitle={myPersonalTitle} id="personalPage">
            <H1>{myPersonalHOne}</H1>
            {isLoadingFeatures && <ListLoadingIndicator data-testid="myPersonalActionListLoadingIndicator" />}
            {!isLoadingFeatures &&
                (!features['postalAddress'] ||
                    !features['phoneNumber'] ||
                    !features['bankAccount'] ||
                    !features['emailAddress']) && (
                    <>
                        {contactPAMessage}
                        <ul>
                            {!features['postalAddress'] && <li>{updateYourPostalAddress}</li>}
                            {!features['phoneNumber'] && <li>{updateYourPhoneNumber}</li>}
                            {!features['bankAccount'] && <li>{updateYourBankAccount}</li>}
                            {!features['emailAddress'] && <li>{updateYourEmailAddress}</li>}
                        </ul>
                    </>
                )}

            {isLoadingMemberData && <CardGridLoadingIndicator data-testid="myPersonalCardGridLoadingIndicator" />}
            {!isLoadingMemberData && (
                <CardGrid>
                    <PaddedCard id="memberBasicInformationCard">
                        <Card.Header headerLevel="2">
                            {member.firstName} {member.lastName} <Tag>{personalTag}</Tag>
                        </Card.Header>
                        <Card.Separator />
                        <Card.Content>
                            <BoldLabel noMarginTop>{dateOfBirthLabel}</BoldLabel>
                            {new Day(member.birthDate).format(language, 'D MMM YYYY')}
                        </Card.Content>
                    </PaddedCard>
                    <PaddedCard id="memberContactInformationCard">
                        <Card.Header>
                            <CardHeaderFlex>
                                <span role="heading" aria-level="2">
                                    {myContactInfo}
                                </span>
                                {(features['postalAddress'] || features['phoneNumber']) && (
                                    <ActionButton
                                        ariaLabel={'Edit ' + myContactInfo}
                                        icon={<EditIcon style={{ color: colors.m_green }} />}
                                        hoverIcon={<EditIcon style={{ color: colors.dark_2_green }} />}
                                        onClick={() => {
                                            sendAdobeAnalyticsTransaction(
                                                ADOBE.TRANSACTION_START,
                                                'Edit My Personal Information'
                                            );
                                            openModal(APP_ROUTES.contactInfoModal);
                                        }}
                                        id="editContactButton"
                                        data-testid="editContactButton"
                                        data-analytics-click="edit-contact-information"
                                    >
                                        {editButton}
                                    </ActionButton>
                                )}
                            </CardHeaderFlex>
                        </Card.Header>
                        <Card.Separator />
                        <Card.Content>
                            <BoldLabel noMarginTop>{addressLabel}</BoldLabel>

                            {member.postalAddress?.streetAddress ? (
                                <>
                                    <span>{member.postalAddress.streetAddress}</span>
                                    {member.postalAddress.suite && (
                                        <AddressLine2Text>{member.postalAddress.suite.toUpperCase()}</AddressLine2Text>
                                    )}
                                    <AddressLine3Text>
                                        {member.postalAddress.city?.toUpperCase()},{' '}
                                        {member.postalAddress.stateOrProvince?.toUpperCase()},{' '}
                                        {member.postalAddress.postalOrZipCode?.toUpperCase()}
                                    </AddressLine3Text>
                                </>
                            ) : (
                                <>
                                    {features['postalAddress'] ? (
                                        <Button
                                            id="addPostalAddress"
                                            data-testid="addPostalAddress"
                                            variant={Button.VARIANT.SECONDARY}
                                            customStyle={{
                                                buttonStyle: { margin: 'auto' },
                                            }}
                                            onClick={() => openModal(APP_ROUTES.contactInfoModal)}
                                        >
                                            {addAddressButton}
                                        </Button>
                                    ) : (
                                        <CtaMsg id="postalAddressCtaMsg">{contactPaMessage}</CtaMsg>
                                    )}
                                </>
                            )}

                            <BoldLabel>{phoneNumberLabel}</BoldLabel>
                            {member.postalAddress?.phoneNumber ? (
                                // Strip the non-digit characters then format to telephone number
                                member.postalAddress.phoneNumber
                                    .replace(/\D/g, '')
                                    .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
                            ) : (
                                <>
                                    {features['phoneNumber'] ? (
                                        <Button
                                            id="addPhoneNumber"
                                            data-testid="addPhoneNumber"
                                            variant={Button.VARIANT.SECONDARY}
                                            customStyle={{
                                                buttonStyle: { margin: 'auto' },
                                            }}
                                            onClick={() => openModal(APP_ROUTES.contactInfoModal)}
                                        >
                                            {addPhoneNumberButton}
                                        </Button>
                                    ) : (
                                        <CtaMsg id="phoneNumberCtaMsg">{contactPaMessage}</CtaMsg>
                                    )}
                                </>
                            )}
                        </Card.Content>
                    </PaddedCard>
                    <PaddedCard id="memberBankInformationCard">
                        <Card.Header>
                            <CardHeaderFlex>
                                <span role="heading" aria-level="2">
                                    {myBankingInfo}
                                </span>
                                {features['bankAccount'] && (
                                    <ActionButton
                                        ariaLabel={'Edit ' + myBankingInfo}
                                        icon={<EditIcon style={{ color: colors.m_green }} />}
                                        hoverIcon={<EditIcon style={{ color: colors.dark_2_green }} />}
                                        onClick={() => {
                                            sendAdobeAnalyticsTransaction(
                                                ADOBE.TRANSACTION_START,
                                                'Edit My Personal Information'
                                            );
                                            openModal(APP_ROUTES.bankingModal);
                                        }}
                                        id="editBankButton"
                                        data-testid="editBankButton"
                                        data-analytics-click="edit-banking-information"
                                    >
                                        {editButton}
                                    </ActionButton>
                                )}
                            </CardHeaderFlex>
                        </Card.Header>
                        <Card.Separator />
                        {member.bankAccount ? (
                            <Card.Content>
                                <BoldLabel noMarginTop>{bankNameLabel}</BoldLabel>
                                {member.bankAccount.bankName}
                                <BankLabelWrapper>
                                    <span>
                                        <BoldLabel>{branchLabel}</BoldLabel>
                                        {member.bankAccount.branchNumber}
                                    </span>

                                    <span>
                                        <BoldLabel>{institutionLabel}</BoldLabel>
                                        {member.bankAccount.institutionNumber}
                                    </span>

                                    <span>
                                        <BoldLabel>{accountLabel}</BoldLabel>
                                        {member.bankAccount.accountNumber.replaceAll(/\d/g, 'x')}
                                    </span>
                                </BankLabelWrapper>
                            </Card.Content>
                        ) : (
                            <Card.Content>
                                {features['bankAccount'] ? (
                                    <Button
                                        id="addBankAccount"
                                        data-testid="addBankAccount"
                                        variant={Button.VARIANT.SECONDARY}
                                        customStyle={{
                                            buttonStyle: { margin: 'auto' },
                                        }}
                                        onClick={() => openModal(APP_ROUTES.bankingModal)}
                                    >
                                        {addBankButton}
                                    </Button>
                                ) : (
                                    <CtaMsg>{bankPaMessage}</CtaMsg>
                                )}
                            </Card.Content>
                        )}
                    </PaddedCard>
                    <PaddedCard id="memberEmailInformationCard">
                        <Card.Header>
                            <CardHeaderFlex>
                                <span role="heading" aria-level="2">
                                    {myEmailAddressInfo}
                                </span>
                                {features['emailAddress'] && (
                                    <ActionButton
                                        ariaLabel={'Edit ' + myEmailAddressInfo}
                                        icon={<EditIcon style={{ color: colors.m_green }} />}
                                        hoverIcon={<EditIcon style={{ color: colors.dark_2_green }} />}
                                        onClick={() => {
                                            sendAdobeAnalyticsTransaction(
                                                ADOBE.TRANSACTION_START,
                                                'Edit My Personal Information'
                                            );
                                            openModal(APP_ROUTES.emailAddressModal);
                                        }}
                                        id="editEmailButton"
                                        data-testid="editEmailButton"
                                        data-analytics-click="edit-email-information"
                                    >
                                        {editButton}
                                    </ActionButton>
                                )}
                            </CardHeaderFlex>
                        </Card.Header>
                        <Card.Separator />
                        {member.emailAddress ? (
                            <Card.Content>
                                <BoldLabel noMarginTop>{emailAddressLabel}</BoldLabel>
                                {member.emailAddress}
                            </Card.Content>
                        ) : (
                            <Card.Content>
                                {features['emailAddress'] ? (
                                    <Button
                                        id="addEmailAddress"
                                        data-testid="addEmailAddress"
                                        variant={Button.VARIANT.SECONDARY}
                                        customStyle={{
                                            buttonStyle: { margin: 'auto' },
                                        }}
                                        onClick={() => {
                                            sendAdobeAnalyticsTransaction(
                                                ADOBE.TRANSACTION_START,
                                                'Edit My Personal Information'
                                            );
                                            openModal(APP_ROUTES.emailAddressModal);
                                        }}
                                    >
                                        {addEmailButton}
                                    </Button>
                                ) : (
                                    <CtaMsg>{emailPaMessage}</CtaMsg>
                                )}
                            </Card.Content>
                        )}
                    </PaddedCard>
                </CardGrid>
            )}
            <Route path={APP_ROUTES.emailAddressModal.url}>
                <EmailAddressModal />
            </Route>
            <Route path={APP_ROUTES.bankingModal.url}>
                <BankingModal />
            </Route>
            <Route path={APP_ROUTES.contactInfoModal.url}>
                <ContactInfoModal />
            </Route>
        </Layout>
    );
};

export default Personal;
